import { supabase } from '@/lib/supabase';
import { WpAppliedChange } from '@/types/api/seo';

export type ChangeImpact = {
  changeId: string;
  changeType: string;
  pageUrl: string | null;
  keyword: string | null;
  positionBefore: number | null;
  positionAfter: number | null;
  positionDelta: number | null;
  ctrBefore: number | null;
  ctrAfter: number | null;
  clicksBefore: number | null;
  clicksAfter: number | null;
  appliedAt: string;
  daysElapsed: number;
  newValue: string;
};

export type ChangeTypeInsight = {
  changeType: string;
  label: string;
  totalChanges: number;
  measuredChanges: number;
  avgPositionDelta: number | null;
  avgCtrDelta: number | null;
  avgClicksDelta: number | null;
  bestChange: ChangeImpact | null;
  worstChange: ChangeImpact | null;
  changes: ChangeImpact[];
};

export type ProjectInsights = {
  totalChanges: number;
  measuredChanges: number;
  avgPositionImprovement: number | null;
  byChangeType: ChangeTypeInsight[];
  topPerformers: ChangeImpact[];
  recentChanges: ChangeImpact[];
};

const changeTypeLabels: Record<string, string> = {
  title_tag: 'Title Tag Updates',
  meta_description: 'Meta Description Updates',
  content: 'Content Additions',
  slug: 'Slug Changes',
};

const seoInsightsService = {
  async getProjectInsights(projectId: string): Promise<ProjectInsights> {
    // Fetch all applied changes with their metrics
    const { data: changes, error } = await supabase
      .from('seo_wp_applied_changes')
      .select('*, seo_tracked_keywords(keyword)')
      .eq('project_id', projectId)
      .order('applied_at', { ascending: false });

    if (error) throw error;

    const allChanges: ChangeImpact[] = (changes ?? []).map((c: any) => {
      const daysElapsed = Math.floor((Date.now() - new Date(c.applied_at).getTime()) / 86400000);
      return {
        changeId: c.id,
        changeType: c.change_type,
        pageUrl: c.page_url,
        keyword: c.seo_tracked_keywords?.keyword ?? null,
        positionBefore: c.position_before,
        positionAfter: c.position_after,
        positionDelta: c.position_before != null && c.position_after != null
          ? c.position_before - c.position_after  // positive = improved
          : null,
        ctrBefore: c.ctr_before,
        ctrAfter: c.ctr_after,
        clicksBefore: c.clicks_before,
        clicksAfter: c.clicks_after,
        appliedAt: c.applied_at,
        daysElapsed,
        newValue: c.new_value,
      };
    });

    const measured = allChanges.filter((c) => c.positionDelta != null);
    const improved = measured.filter((c) => c.positionDelta! > 0);

    // Group by change type
    const byType = new Map<string, ChangeImpact[]>();
    for (const c of allChanges) {
      const existing = byType.get(c.changeType) ?? [];
      existing.push(c);
      byType.set(c.changeType, existing);
    }

    const byChangeType: ChangeTypeInsight[] = [];
    for (const [type, typeChanges] of byType) {
      const typeMeasured = typeChanges.filter((c) => c.positionDelta != null);
      const avgPos = typeMeasured.length > 0
        ? typeMeasured.reduce((s, c) => s + c.positionDelta!, 0) / typeMeasured.length
        : null;
      const avgCtr = typeMeasured.filter((c) => c.ctrBefore != null && c.ctrAfter != null).length > 0
        ? typeMeasured.filter((c) => c.ctrBefore != null && c.ctrAfter != null)
            .reduce((s, c) => s + (c.ctrAfter! - c.ctrBefore!), 0) / typeMeasured.filter((c) => c.ctrBefore != null && c.ctrAfter != null).length
        : null;
      const avgClicks = typeMeasured.filter((c) => c.clicksBefore != null && c.clicksAfter != null).length > 0
        ? typeMeasured.filter((c) => c.clicksBefore != null && c.clicksAfter != null)
            .reduce((s, c) => s + (c.clicksAfter! - c.clicksBefore!), 0) / typeMeasured.filter((c) => c.clicksBefore != null && c.clicksAfter != null).length
        : null;

      const sorted = [...typeMeasured].sort((a, b) => (b.positionDelta ?? 0) - (a.positionDelta ?? 0));

      byChangeType.push({
        changeType: type,
        label: changeTypeLabels[type] ?? type,
        totalChanges: typeChanges.length,
        measuredChanges: typeMeasured.length,
        avgPositionDelta: avgPos,
        avgCtrDelta: avgCtr,
        avgClicksDelta: avgClicks,
        bestChange: sorted[0] ?? null,
        worstChange: sorted[sorted.length - 1] ?? null,
        changes: typeChanges,
      });
    }

    // Sort by avg impact
    byChangeType.sort((a, b) => (b.avgPositionDelta ?? -999) - (a.avgPositionDelta ?? -999));

    // Top performers (changes with biggest positive impact)
    const topPerformers = [...measured]
      .sort((a, b) => (b.positionDelta ?? 0) - (a.positionDelta ?? 0))
      .slice(0, 5);

    return {
      totalChanges: allChanges.length,
      measuredChanges: measured.length,
      avgPositionImprovement: measured.length > 0
        ? measured.reduce((s, c) => s + c.positionDelta!, 0) / measured.length
        : null,
      byChangeType,
      topPerformers,
      recentChanges: allChanges.slice(0, 10),
    };
  },

  /**
   * Get the best-performing change types for this project.
   * Used by the playbook to prioritize steps.
   */
  async getBestChangeTypes(projectId: string): Promise<Map<string, number>> {
    const insights = await this.getProjectInsights(projectId);
    const map = new Map<string, number>();
    for (const ct of insights.byChangeType) {
      if (ct.avgPositionDelta != null && ct.measuredChanges >= 1) {
        map.set(ct.changeType, ct.avgPositionDelta);
      }
    }
    return map;
  },

  /**
   * Update after-metrics for changes that are old enough.
   * Call this when GSC data loads to fill in the "after" measurements.
   */
  async measurePendingChanges(
    projectId: string,
    currentMetrics: { position: number | null; ctr: number | null; clicks: number | null; impressions: number | null }
  ): Promise<number> {
    // Find changes older than 7 days that haven't been measured
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    const { data: pending } = await supabase
      .from('seo_wp_applied_changes')
      .select('id')
      .eq('project_id', projectId)
      .eq('impact_status', 'pending')
      .lt('applied_at', sevenDaysAgo.toISOString())
      .is('position_after', null);

    if (!pending || pending.length === 0) return 0;

    let measured = 0;
    for (const change of pending) {
      let impactStatus = 'insufficient_data';
      if (currentMetrics.position != null) {
        const { data: row } = await supabase
          .from('seo_wp_applied_changes')
          .select('position_before')
          .eq('id', change.id)
          .single();

        if (row?.position_before != null) {
          const delta = row.position_before - currentMetrics.position;
          if (Math.abs(delta) < 0.5) impactStatus = 'no_change';
          else impactStatus = delta > 0 ? 'improved' : 'declined';
        }
      }

      await supabase
        .from('seo_wp_applied_changes')
        .update({
          position_after: currentMetrics.position,
          ctr_after: currentMetrics.ctr,
          clicks_after: currentMetrics.clicks,
          impressions_after: currentMetrics.impressions,
          after_measured_at: new Date().toISOString(),
          impact_status: impactStatus,
        })
        .eq('id', change.id);

      measured++;
    }

    return measured;
  },
};

export default seoInsightsService;
